import { generateProof } from 'dpop';
import { getBackendOrigin } from './backend-origin';
import { getOrCreateKeyPair, getPublicJwk } from './dpop';
import { getUser } from '../store/userStore';

const AUTH_PATH = '/auth/access-token';

interface AccessTokenResponse {
    accessToken: string;
    expiresInSeconds: number;
}

interface TokenCache {
    expiresAt: number; // epoch seconds
    token: string;
}

let cachedToken: TokenCache | undefined = undefined;
let cachedError: Error | undefined;

/**
 * Access Token beim Backend holen (Bootstrap).
 * DPoP-Proof OHNE jti (includeJti: false).
 */
const fetchAccessToken = async (): Promise<TokenCache> => {
    const user = getUser();
    const response = await fetch(location.origin + AUTH_PATH, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            DPoP: await generateProof(
                await getOrCreateKeyPair(),
                getBackendOrigin() + AUTH_PATH,
                'POST'
            ),
        },
        body: JSON.stringify({
            ...user,
            dpopPublicKeyJwk: await getPublicJwk(),
        }),
    });

    if (!response.ok) {
        const err = new Error(`/auth/access-token failed: ${response.status}`);
        cachedError = err; // ❗ Fehler merken
        cachedToken = undefined;
        throw err;
    }

    const data: AccessTokenResponse = await response.json();
    const now = Math.floor(Date.now() / 1000);

    cachedToken = {
        token: data.accessToken,
        expiresAt: now + data.expiresInSeconds - 5,
    };
    cachedError = undefined; // Erfolg → Fehler zurücksetzen
    return cachedToken;
};

/**
 * Token holen (aus Cache oder via Bootstrap neu)
 */
export const getValidAccessToken = async (): Promise<string> => {
    // Wenn das Bootstrapping schon mit Fehler geendet hat,
    // nicht dauernd neue Requests losschicken:
    if (cachedError) {
        throw cachedError;
    }

    const now = Math.floor(Date.now() / 1000);
    if (cachedToken && cachedToken.expiresAt > now) {
        return cachedToken.token;
    }

    // Kein (gültiger) Token → einmal versuchen, kann wieder cachedError setzen
    return (await fetchAccessToken()).token;
};

/**
 * Optional: Token-Reset bei 401 etc.
 */
export const resetAccessToken = () => {
    cachedToken = undefined;
    cachedError = undefined;
};
